/**
 * Rebuy Upsell Block
 *
 * Container + toggle only. The product cards and add-to-cart inside each panel
 * are rendered by Rebuy, which mounts into the [data-rebuy-id] div in each panel.
 * This component never rewrites that markup - it shows and hides panels, and
 * optionally redirects a panel's button to the product page.
 */
(function () {
  const selectors = {
    tab: '[data-rebuy-upsell-tab]',
    panel: '[data-rebuy-upsell-panel]',
    linkedPanel: '[data-rebuy-upsell-link]',
    rebuyButton: '.rebuy-button',
    rebuyCard: '.rebuy-product-block',
    rebuyProductLink: '.rebuy-product-title-link, .rebuy-product-image',
    rebuyWidget: '.rebuy-widget',
  };

  const classes = {
    // Rebuy marks a widget that resolved to zero products
    rebuyEmpty: 'is-hidden',
  };

  const attributes = {
    empty: 'data-rebuy-upsell-empty',
  };

  // A late safety-net re-check, in case Rebuy settles without a mutation we saw.
  const EMPTY_RECHECK_MS = 15000;

  const keys = {
    left: 'ArrowLeft',
    right: 'ArrowRight',
    home: 'Home',
    end: 'End',
  };

  class RebuyUpsellTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll(selectors.tab));
      this.panels = Array.from(this.querySelectorAll(selectors.panel));

      // These apply whether or not there are tabs to toggle between.
      this.bindProductLinks();
      this.watchForEmptyWidgets();

      // One panel (or none) means there is nothing to toggle between.
      if (this.tabs.length < 2 || this.tabs.length !== this.panels.length) return;

      // Panels that have been on screen at least once. Rebuy mounts its widgets
      // on page load whether or not the panel is hidden, but a widget that first
      // laid itself out while hidden can have stale measurements, so each panel
      // gets a nudge the first time it is revealed.
      this.revealed = new Set();
      const active = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      this.activeIndex = active === -1 ? 0 : active;
      this.revealed.add(this.activeIndex);

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
    }

    /**
     * On a panel marked [data-rebuy-upsell-link], send the Rebuy button to the
     * product page instead of adding to cart.
     *
     * Listening in the capture phase on this element, rather than rewriting the
     * button, is deliberate: Rebuy re-renders its cards (variant changes, cart
     * updates), which would undo any markup we changed. A delegated capture
     * listener keeps working, and stopping the event here prevents Rebuy's own
     * click handler on the button from firing.
     */
    bindProductLinks() {
      if (!this.querySelector(selectors.linkedPanel)) return;

      this.addEventListener(
        'click',
        (event) => {
          const button = event.target.closest(selectors.rebuyButton);
          if (!button || !this.contains(button)) return;

          const panel = button.closest(selectors.linkedPanel);
          if (!panel) return;

          // Scope to this card, so a multi-product widget links each card correctly.
          const card = button.closest(selectors.rebuyCard) || panel;
          const link = card.querySelector(selectors.rebuyProductLink);
          const href = link && link.getAttribute('href');

          // No URL to send them to - leave Rebuy's own behaviour alone.
          if (!href) return;

          event.preventDefault();
          event.stopPropagation();
          window.location.href = link.href;
        },
        true
      );
    }

    onClick(event) {
      const tab = event.target.closest(selectors.tab);
      if (!tab || !this.contains(tab)) return;

      event.preventDefault();
      this.select(this.tabs.indexOf(tab));
    }

    onKeydown(event) {
      const tab = event.target.closest(selectors.tab);
      if (!tab || !this.contains(tab)) return;

      const last = this.tabs.length - 1;
      const current = this.tabs.indexOf(tab);
      let next = null;

      switch (event.key) {
        case keys.left:
          next = current === 0 ? last : current - 1;
          break;
        case keys.right:
          next = current === last ? 0 : current + 1;
          break;
        case keys.home:
          next = 0;
          break;
        case keys.end:
          next = last;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.select(next, true);
    }

    /**
     * Hide a tab whose Rebuy widget came back with no products, and hide the
     * whole block when none of them have any.
     *
     * Rebuy mounts asynchronously and re-renders when the cart changes, so this
     * re-evaluates on mutation rather than once. It fails open: until Rebuy has
     * mounted a widget element we leave everything visible, so a Rebuy outage
     * or a script error never blanks a working upsell.
     */
    watchForEmptyWidgets() {
      const isEmpty = (panel) => {
        const widget = panel.querySelector(selectors.rebuyWidget);
        // Not mounted yet - not empty, just not ready.
        if (!widget) return false;
        if (widget.classList.contains(classes.rebuyEmpty)) return true;
        return panel.querySelectorAll(selectors.rebuyCard).length === 0;
      };

      const evaluate = () => {
        const empties = this.panels.map(isEmpty);

        this.panels.forEach((panel, i) => {
          panel.toggleAttribute(attributes.empty, empties[i]);
          if (this.tabs[i]) this.tabs[i].toggleAttribute(attributes.empty, empties[i]);
        });

        const block = this.closest('.rebuy-upsell') || this;
        const allEmpty = empties.length > 0 && empties.every(Boolean);
        block.toggleAttribute(attributes.empty, allEmpty);

        // With one side gone there is nothing to toggle: make the survivor the
        // selected tab so it reads as the single static label. The empty tab is
        // display:none, so it leaves the accessibility tree entirely. Kept
        // idempotent and reversible - a cart-driven re-render can refill a
        // widget, and this then hands the pair back intact.
        if (this.tabs.length > 1 && empties.filter((e) => !e).length === 1) {
          const survivor = empties.findIndex((e) => !e);
          this.tabs.forEach((tab, i) => {
            const isActive = i === survivor;
            tab.classList.toggle('is-active', isActive);
            tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
            tab.setAttribute('tabindex', isActive ? '0' : '-1');
            this.panels[i].toggleAttribute('hidden', !isActive);
          });
          this.activeIndex = survivor;
        }
      };

      evaluate();

      const observer = new MutationObserver(evaluate);
      this.panels.forEach((panel) =>
        observer.observe(panel, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] })
      );

      // The observer stays attached so cart-driven re-renders keep being handled;
      // this is just a late re-check in case a change slipped past it.
      setTimeout(evaluate, EMPTY_RECHECK_MS);
    }

    select(index, moveFocus) {
      if (index === this.activeIndex || !this.tabs[index]) return;

      this.tabs.forEach((tab, i) => {
        const isActive = i === index;
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        // Roving tabindex: only the selected tab is in the tab order.
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
        tab.classList.toggle('is-active', isActive);
        this.panels[i].toggleAttribute('hidden', !isActive);
      });

      this.activeIndex = index;

      if (moveFocus) this.tabs[index].focus();

      if (!this.revealed.has(index)) {
        this.revealed.add(index);
        // Let the panel lay out, then prompt anything inside it that measured
        // itself while hidden (a Rebuy carousel, for example) to recalculate.
        requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
      }

      this.dispatchEvent(
        new CustomEvent('rebuy-upsell:tab-changed', {
          bubbles: true,
          detail: { index, panel: this.panels[index] },
        })
      );
    }
  }

  if (!customElements.get('rebuy-upsell-tabs')) {
    customElements.define('rebuy-upsell-tabs', RebuyUpsellTabs);
  }
})();
